import * as React from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { fetchTherapistPatients } from "../../Services/apiService";
import PortalPagination from "../../components/portal/PortalPagination";
import PillSearchField from "../../components/PillSearchField";
import { C, compactCardSx } from "../../theme/dashboardCardTokens";
import { useTranslation } from "../../i18n/LanguageContext";

// One row per patient (grouped by first_name/last_name/date_of_birth on the
// backend — see the doc's known-limitation note), not one row per order.
// Click through to that patient's orders via patient_key, passed un-decoded.
function TherapistPatients() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [patients, setPatients] = React.useState([]);
  const [pagination, setPagination] = React.useState(null);
  const [page, setPage] = React.useState(1);
  const [searchInput, setSearchInput] = React.useState("");
  const [debouncedSearch, setDebouncedSearch] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");

  // Debounce search input (400ms), same as the admin Orders page.
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchTherapistPatients(page, 20, debouncedSearch)
      .then((res) => {
        if (cancelled) return;
        setPatients(res.data?.patients || []);
        setPagination(res.data?.pagination || null);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err.status === 403) {
          navigate("/dashboard", { replace: true });
          return;
        }
        console.error("fetchTherapistPatients failed:", { status: err.status, message: err.message, err });
        setError(err.message || t("portal.patients.loadError"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, debouncedSearch, navigate]);

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
        {t("portal.patients.title")}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t("portal.patients.subtitle")}
      </Typography>

      <PillSearchField
        value={searchInput}
        onChange={setSearchInput}
        placeholder={t("portal.patients.searchPlaceholder")}
        resultCount={!loading ? pagination?.total : null}
        loading={loading}
        sx={{ mb: "20px" }}
      />

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {!loading && error && <Alert severity="error">{error}</Alert>}

      {!loading && !error && patients.length === 0 && (
        <Box sx={{ p: 4, textAlign: "center", color: C.textSecondary }}>
          {debouncedSearch ? t("portal.patients.emptySearch") : t("portal.patients.emptyAll")}
        </Box>
      )}

      {!loading && !error && patients.length > 0 && (
        <TableContainer component={Paper} sx={{ ...compactCardSx, overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("portal.patients.table.patient")}</TableCell>
                <TableCell>{t("portal.patients.table.dateOfBirth")}</TableCell>
                <TableCell>{t("portal.patients.table.tests")}</TableCell>
                <TableCell>{t("portal.patients.table.latestTest")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {patients.map((patient) => (
                <TableRow
                  key={patient.patient_key}
                  hover
                  onClick={() => navigate(`/therapist/patients/${patient.patient_key}`)}
                  sx={{ cursor: "pointer" }}
                >
                  <TableCell>
                    {`${patient.first_name || ""} ${patient.last_name || ""}`.trim() || "—"}
                  </TableCell>
                  <TableCell>{patient.date_of_birth || "—"}</TableCell>
                  <TableCell>{patient.test_count}</TableCell>
                  <TableCell>
                    {patient.latest_test_date ? dayjs(patient.latest_test_date).format("MMM D, YYYY") : "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!loading && !error && <PortalPagination pagination={pagination} onChange={setPage} />}
    </Box>
  );
}

export default TherapistPatients;
